// Package imports
import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';

// CSS imports
import './Navigation.css';

// Providers
import { ViewportContext, Viewport } from '../../providers/ViewportProvider';

// Icons
import { PiSignOutBold } from 'react-icons/pi';

// Components
import FloatingDropdown from '../floatingDropdown/FloatingDropdown';
import StaticDropdown from '../staticDropdown/StaticDropdown';
import PrimaryButton from '../buttons/primaryButton/PrimaryButton';

// Helpers
import Height100Div from '../../helpers/components/height_100_div/Height100Div';
import { checkAdminAccess } from '../../helpers/functions/checkAdminAccess';

// Custom Images
import DefaultUser from '../../assets/images/defaultUser.jpg';
import Logo from '../../assets/images/logo.png';

// Service imports
import { ApiService } from '../../services/ApiService';

// Type imports
import { User } from '../../types/user/User';
import { NavigationLink } from '../../types/navigation/NavigationLink';
import { DropdownLink } from '../../types/navigation/DropdownLink';
import { DropdownElement } from '../../types/dropdown/DropdownElement';

// Special imports
import { config } from '../../config';

type Props = {
  user: User | null;
  toggleLogin: () => void;
  setUser: (x: User | null) => void;
};

declare const window: any;

const Navigation: React.FC<Props> = ({ user, toggleLogin, setUser }: Props) => {
  const { viewport } = useContext(ViewportContext);
  const [isOpen, setIsOpen] = useState(false);

  const signout = async () => {
    setUser(null);
    await ApiService.logout();
    window.location.reload(false);
  };

  const toggleMobileNav = () => {
    if (isOpen) {
      document.body.style.overflow = 'scroll';
    } else {
      document.body.style.overflow = 'hidden';
    }
    setIsOpen(!isOpen);
  };

  const closeMobileNav = () => {
    document.body.style.overflow = 'scroll';
    setIsOpen(false);
  };

  return (
    <header className='Navigation'>
      <div className='inner-navigation'>
        <Link className='nav-section' to='/' onClick={closeMobileNav}>
          <div className='logo-container'>
            <img src={Logo} alt={config.appName} />
          </div>
          {viewport !== Viewport.MOBILE ? (
            <span className='logo-name'>{config.appName}</span>
          ) : null}
        </Link>
        {viewport === Viewport.DESKTOP ? (
          <>
            <div className='nav-section'>
              {config.links.navigation
                .filter((item: NavigationLink) => {
                  if (item.restricted) {
                    if (
                      checkAdminAccess(user ? user.role : null, item.restricted)
                    ) {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                })
                .map((item: NavigationLink, index: number) => {
                  if (item.link) {
                    return (
                      <Link
                        to={item.link}
                        className='navigation-link nav-margin'
                        key={index}
                      >
                        {item.name}
                      </Link>
                    );
                  } else if (item.items) {
                    return (
                      <FloatingDropdown
                        key={index}
                        label={item.name}
                        elements={item.items
                          .filter((item: DropdownLink) => {
                            if (item.restricted) {
                              if (
                                checkAdminAccess(
                                  user ? user.role : null,
                                  item.restricted
                                )
                              ) {
                                return true;
                              } else {
                                return false;
                              }
                            } else {
                              return true;
                            }
                          })
                          .map((dropdownItem: DropdownLink) => {
                            if (dropdownItem.ReactIcon) {
                              return {
                                to: dropdownItem.link,
                                label: dropdownItem.name,
                                ReactIcon: dropdownItem.ReactIcon,
                              };
                            } else if (dropdownItem.imgIcon) {
                              return {
                                to: dropdownItem.link,
                                label: dropdownItem.name,
                                imgIcon: dropdownItem.imgIcon,
                              };
                            } else {
                              return {
                                to: dropdownItem.link,
                                label: dropdownItem.name,
                              };
                            }
                          })}
                        className='nav-margin'
                      />
                    );
                  } else {
                    return null;
                  }
                })}
            </div>
            <div className='nav-section'>
              {user == null ? (
                <PrimaryButton label='Login' onClick={toggleLogin} />
              ) : (
                <div>
                  <FloatingDropdown
                    label='Profile'
                    openToLeft={true}
                    customLabel={
                      <div className='profile-picture-container'>
                        <img
                          src={DefaultUser}
                          alt='Profile'
                          className='profile-picture'
                        />
                      </div>
                    }
                    elements={[
                      config.links.user
                        .filter((item) => {
                          if (item.restricted) {
                            if (checkAdminAccess(user.role, item.restricted)) {
                              return true;
                            } else {
                              return false;
                            }
                          } else {
                            return true;
                          }
                        })
                        .map((item: DropdownLink): DropdownElement => {
                          if (item.ReactIcon) {
                            return {
                              to: item.link,
                              label: item.name,
                              ReactIcon: item.ReactIcon,
                            };
                          } else if (item.imgIcon) {
                            return {
                              to: item.link,
                              label: item.name,
                              imgIcon: item.imgIcon,
                            };
                          } else {
                            return {
                              to: item.link,
                              label: item.name,
                            };
                          }
                        }),
                      {
                        to: '/signout',
                        label: 'Sign out',
                        isButton: true,
                        action: signout,
                        ReactIcon: PiSignOutBold,
                      },
                    ].flat()}
                  />
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className='nav-section'>
              <div className='menu-container' onClick={toggleMobileNav}>
                <div className={`menu-btn ${isOpen ? 'open' : ''}`}>
                  <div className='menu-btn__burger'></div>
                </div>
              </div>
            </div>
            {isOpen ? (
              <Height100Div className='mobile-menu-sheet'>
                <div>
                  {user != null ? (
                    <div>
                      <StaticDropdown
                        label='Profile'
                        customLabel={
                          <div className='profile-picture-container'>
                            <img
                              src={DefaultUser}
                              alt='Profile'
                              className='profile-picture'
                            />
                          </div>
                        }
                        elements={[
                          config.links.user
                            .filter((item: DropdownLink) => {
                              if (item.restricted) {
                                if (
                                  checkAdminAccess(user.role, item.restricted)
                                ) {
                                  return true;
                                } else {
                                  return false;
                                }
                              } else {
                                return true;
                              }
                            })
                            .map((item: DropdownLink): DropdownElement => {
                              if (item.ReactIcon) {
                                return {
                                  to: item.link,
                                  label: item.name,
                                  ReactIcon: item.ReactIcon,
                                };
                              } else if (item.imgIcon) {
                                return {
                                  to: item.link,
                                  label: item.name,
                                  imgIcon: item.imgIcon,
                                };
                              } else {
                                return {
                                  to: item.link,
                                  label: item.name,
                                };
                              }
                            }),
                          {
                            to: '/signout',
                            label: 'Sign out',
                            isButton: true,
                            action: signout,
                            ReactIcon: PiSignOutBold,
                          },
                        ].flat()}
                        toggleMenu={toggleMobileNav}
                        className='mobile-nav-margin'
                      />
                    </div>
                  ) : null}
                  {config.links.navigation
                    .filter((item: NavigationLink) => {
                      if (item.restricted) {
                        if (
                          checkAdminAccess(
                            user ? user.role : null,
                            item.restricted
                          )
                        ) {
                          return true;
                        } else {
                          return false;
                        }
                      } else {
                        return true;
                      }
                    })
                    .map((item: NavigationLink, index: number) => {
                      if (item.link) {
                        return (
                          <Link
                            key={index}
                            to={item.link}
                            className='mobile-navigation-link mobile-nav-margin'
                            onClick={toggleMobileNav}
                          >
                            {item.name}
                          </Link>
                        );
                      } else if (item.items) {
                        return (
                          <StaticDropdown
                            key={index}
                            label={item.name}
                            elements={item.items
                              .filter((item: DropdownLink) => {
                                if (item.restricted) {
                                  if (
                                    checkAdminAccess(
                                      user ? user.role : null,
                                      item.restricted
                                    )
                                  ) {
                                    return true;
                                  } else {
                                    return false;
                                  }
                                } else {
                                  return true;
                                }
                              })
                              .map((dropdownItem: DropdownLink) => {
                                if (dropdownItem.ReactIcon) {
                                  return {
                                    to: dropdownItem.link,
                                    label: dropdownItem.name,
                                    ReactIcon: dropdownItem.ReactIcon,
                                  };
                                } else if (dropdownItem.imgIcon) {
                                  return {
                                    to: dropdownItem.link,
                                    label: dropdownItem.name,
                                    imgIcon: dropdownItem.imgIcon,
                                  };
                                } else {
                                  return {
                                    to: dropdownItem.link,
                                    label: dropdownItem.name,
                                  };
                                }
                              })}
                            className='mobile-nav-margin'
                            toggleMenu={toggleMobileNav}
                          />
                        );
                      } else {
                        return null;
                      }
                    })}
                </div>
                <div className='mobile-buttons-container'>
                  {user == null ? (
                    <PrimaryButton label='Login' onClick={toggleLogin} />
                  ) : null}
                </div>
              </Height100Div>
            ) : null}
          </>
        )}
      </div>
    </header>
  );
};

export default Navigation;
